import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  BarChart3,
  TrendingUp,
  Plane,
  DollarSign,
  Users,
  Calendar,
  MapPin,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReportsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/bookings?limit=500`);
      if (response.data.success) {
        setBookings(response.data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings by date range
  const getFilteredBookings = () => {
    const now = new Date();
    return bookings.filter(b => {
      const bookingDate = new Date(b.created_at);
      if (dateRange === 'week') {
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return bookingDate >= weekAgo;
      } else if (dateRange === 'month') {
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return bookingDate >= monthAgo;
      } else if (dateRange === 'year') {
        const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        return bookingDate >= yearAgo;
      }
      return true;
    });
  };

  const filteredBookings = getFilteredBookings();

  // Calculate stats
  const totalRevenue = filteredBookings.reduce((sum, b) => sum + (b.flight_data?.price || 0), 0);
  const avgBookingValue = filteredBookings.length > 0 ? totalRevenue / filteredBookings.length : 0;
  const totalPassengers = filteredBookings.reduce((sum, b) => sum + (b.passengers?.length || 0), 0);

  // Bookings by airline
  const bookingsByAirline = filteredBookings.reduce((acc, b) => {
    const airline = b.flight_data?.airline || 'Unknown';
    acc[airline] = (acc[airline] || 0) + 1;
    return acc;
  }, {});

  // Revenue by airline
  const revenueByAirline = filteredBookings.reduce((acc, b) => {
    const airline = b.flight_data?.airline || 'Unknown';
    acc[airline] = (acc[airline] || 0) + (b.flight_data?.price || 0);
    return acc;
  }, {});

  // Bookings by route
  const bookingsByRoute = filteredBookings.reduce((acc, b) => {
    const route = `${b.flight_data?.from || '?'} → ${b.flight_data?.to || '?'}`;
    acc[route] = (acc[route] || 0) + 1;
    return acc;
  }, {});

  // Bookings by day (last 7 days)
  const bookingsByDay = {};
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
    bookingsByDay[dateStr] = 0;
  }
  filteredBookings.forEach(b => {
    const date = new Date(b.created_at);
    const dateStr = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
    if (bookingsByDay.hasOwnProperty(dateStr)) {
      bookingsByDay[dateStr]++;
    }
  });

  const maxDayBookings = Math.max(...Object.values(bookingsByDay), 1);

  // Sort and get top items
  const topAirlines = Object.entries(bookingsByAirline)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const topRoutes = Object.entries(bookingsByRoute)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const topRevenueAirlines = Object.entries(revenueByAirline)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const exportReport = () => {
    const report = {
      generated: new Date().toISOString(),
      dateRange,
      summary: {
        totalBookings: filteredBookings.length,
        totalRevenue,
        avgBookingValue,
        totalPassengers
      },
      bookingsByAirline,
      revenueByAirline,
      bookingsByRoute
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${dateRange}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-4" />
        <p className="text-gray-500">Loading reports...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-500" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchBookings}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportReport}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold">{filteredBookings.length}</p>
              </div>
              <Plane className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold">£{totalRevenue.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              </div>
              <DollarSign className="h-10 w-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Avg Booking Value</p>
                <p className="text-3xl font-bold">£{avgBookingValue.toFixed(0)}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Total Passengers</p>
                <p className="text-3xl font-bold">{totalPassengers}</p>
              </div>
              <Users className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            Bookings Trend (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-2 h-48">
            {Object.entries(bookingsByDay).map(([day, count]) => (
              <div key={day} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                  style={{ height: `${(count / maxDayBookings) * 100}%`, minHeight: count > 0 ? '20px' : '4px' }}
                />
                <span className="text-xs text-gray-500 mt-2">{day}</span>
                <span className="text-xs font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Top Airlines by Bookings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plane className="h-4 w-4 text-blue-500" />
              Top Airlines (Bookings)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topAirlines.length === 0 ? (
                <p className="text-gray-500 text-sm">No data available</p>
              ) : (
                topAirlines.map(([airline, count], idx) => (
                  <div key={airline} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-50 text-blue-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[150px]">{airline}</span>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{count}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Airlines by Revenue */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-500" />
              Top Airlines (Revenue)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topRevenueAirlines.length === 0 ? (
                <p className="text-gray-500 text-sm">No data available</p>
              ) : (
                topRevenueAirlines.map(([airline, revenue], idx) => (
                  <div key={airline} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-amber-100 text-amber-700' :
                        'bg-green-50 text-green-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium truncate max-w-[150px]">{airline}</span>
                    </div>
                    <span className="text-sm font-semibold text-green-600">£{revenue.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Routes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-purple-500" />
              Top Routes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topRoutes.length === 0 ? (
                <p className="text-gray-500 text-sm">No data available</p>
              ) : (
                topRoutes.map(([route, count], idx) => (
                  <div key={route} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                        idx === 1 ? 'bg-gray-100 text-gray-700' :
                        idx === 2 ? 'bg-amber-100 text-amber-700' :
                        'bg-purple-50 text-purple-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium">{route}</span>
                    </div>
                    <span className="text-sm font-semibold text-purple-600">{count}</span>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsPanel;
